import { FormikProps } from 'formik'
import { IPositionNameSectionFormValues } from '@src/features/shift-post-v2/steps/shift-details-step/components/position-name-section/position-name-section.types'
import { useEffect, useState } from 'react'
import {
  amountLiftedFrequentlyDropdownData,
  amountLiftedOccasionallyDropdownData,
  mapFieldFromValue,
} from '@src/helpers/shiftHelpers/physicalRequirementsUtils'
import { IDropdownData } from '@network/models/common.model'

interface IPhysicalRequirements {
  amountLiftedFrequently: IDropdownData
  amountLiftedOccasionally: IDropdownData
  amountOfTimeSpentOnFeet: IDropdownData
}

export const usePhysicalRequirements = (
  formik: FormikProps<IPositionNameSectionFormValues>,
) => {
  const { values, setFieldValue, validateField } = formik
  const [physicalReqsErrors, setPhysReqsErrors] = useState<{
    amountOfTimeSpentOnFeet?: string
    amountLiftedFrequently?: string
    amountLiftedOccasionally?: string
  }>({})

  const [physicalRequirements, setPhysicalRequirements] = useState<
    IPhysicalRequirements
  >({
    amountLiftedFrequently: mapFieldFromValue(
      'amount_lifted_frequently',
      values.amount_lifted_frequently || 0,
    ),
    amountLiftedOccasionally: mapFieldFromValue(
      'amount_lifted_occasionally',
      values.amount_lifted_occasionally || 0,
    ),
    amountOfTimeSpentOnFeet: mapFieldFromValue(
      'amount_of_time_spent_on_feet',
      Number(values.amount_of_time_spent_on_feet),
    ),
  })

  const resetPhysicalRequirements = () => {
    setPhysicalRequirements({
      amountLiftedFrequently: mapFieldFromValue(
        'amount_lifted_frequently',
        values.amount_lifted_frequently || 0,
      ),
      amountLiftedOccasionally: mapFieldFromValue(
        'amount_lifted_occasionally',
        values.amount_lifted_occasionally || 0,
      ),
      amountOfTimeSpentOnFeet: mapFieldFromValue(
        'amount_of_time_spent_on_feet',
        Number(values.amount_of_time_spent_on_feet),
      ),
    })
  }

  // Set initial values
  useEffect(() => {
    setPhysicalRequirements({
      amountLiftedFrequently: mapFieldFromValue(
        'amount_lifted_frequently',
        values.amount_lifted_frequently || 0,
      ),
      amountLiftedOccasionally: mapFieldFromValue(
        'amount_lifted_occasionally',
        values.amount_lifted_occasionally || 0,
      ),
      amountOfTimeSpentOnFeet: mapFieldFromValue(
        'amount_of_time_spent_on_feet',
        Number(values.amount_of_time_spent_on_feet),
      ),
    })
  }, [
    values.amount_lifted_frequently,
    values.amount_lifted_occasionally,
    values.amount_of_time_spent_on_feet,
  ])

  useEffect(() => {
    const validationRules = {
      amountOfTimeSpentOnFeet: 'Amount of time spent on feet is required',
      amountLiftedFrequently: 'Amount lifted frequently is required',
      amountLiftedOccasionally: 'Amount lifted occasionally is required',
    } as const

    const newErrors = Object.entries(validationRules).reduce(
      (errors, [field, message]) => {
        const fieldKey = field as keyof typeof validationRules
        errors[fieldKey] = physicalRequirements[fieldKey] ? undefined : message
        return errors
      },
      {} as typeof physicalReqsErrors,
    )

    setPhysReqsErrors(newErrors)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    physicalRequirements.amountLiftedFrequently,
    physicalRequirements.amountLiftedOccasionally,
    physicalRequirements.amountOfTimeSpentOnFeet,
  ])

  const updatePhysicalRequirements = (
    field: keyof IPhysicalRequirements,
    value: IDropdownData,
  ) => {
    const isAmountLiftedFrequentlySetToMax =
      field === 'amountLiftedFrequently' &&
      value === amountLiftedFrequentlyDropdownData[0]

    // If the amount lifted frequently is set to the max, set the amount lifted occasionally to the max as well
    if (isAmountLiftedFrequentlySetToMax) {
      setPhysicalRequirements(prev => ({
        ...prev,
        amountLiftedFrequently: amountLiftedFrequentlyDropdownData[0],
        amountLiftedOccasionally: amountLiftedOccasionallyDropdownData[0],
      }))

      return
    }

    setPhysicalRequirements(prev => ({ ...prev, [field]: value }))
  }

  const setPhysicalRequirementsInFormik = async () => {
    await setFieldValue(
      'amount_lifted_frequently',
      physicalRequirements.amountLiftedFrequently.value,
    )
    await setFieldValue(
      'amount_lifted_occasionally',
      physicalRequirements.amountLiftedOccasionally.value,
    )
    await setFieldValue(
      'amount_of_time_spent_on_feet',
      physicalRequirements.amountOfTimeSpentOnFeet.value,
    )

    // Trigger validation for these fields to clear any existing errors
    setTimeout(() => {
      validateField('amount_lifted_frequently')
      validateField('amount_lifted_occasionally')
      validateField('amount_of_time_spent_on_feet')
    }, 0)
  }

  return {
    ...physicalRequirements,
    updatePhysicalRequirements,
    resetPhysicalRequirements,
    setPhysicalRequirementsInFormik,
    physicalReqsErrors,
  }
}
